import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, Loader2, RotateCcw, TriangleAlert } from 'lucide-react';
import { TableSkeleton } from '../../../Common/Skeleton';
import { Button } from '../detail/primitives';

/*
 * The orders table.
 *
 * Rows are memoised and their handlers bound per row, so ticking one checkbox
 * no longer re-renders every row on the page. Previously selection lived at the
 * top with no memoisation and each row mounted a react-select, so toggling one
 * box re-rendered up to a hundred rows and every select inside them.
 *
 * The header and column widths stay mounted through loading, empty and error
 * states — only the body swaps. That keeps the table from collapsing to nothing
 * and springing back, which is the thing that reads as jank.
 */

const Checkbox = ({ checked, indeterminate, onChange, label }) => (
  <input
    type="checkbox"
    aria-label={label}
    checked={checked}
    ref={(node) => {
      if (node) node.indeterminate = Boolean(indeterminate);
    }}
    onChange={onChange}
    className="w-3.5 h-3.5 cursor-pointer accent-nysmah-accent align-middle"
  />
);

/*
 * Anything the row-click handler must not hijack. The order number is itself an
 * <a> whose hit area is stretched across its cell, so a click there matches
 * this list and falls through to the anchor's native behaviour — which is how
 * cmd-click and middle-click keep working.
 */
const INTERACTIVE = 'a, button, input, select, textarea, label, [role="combobox"], [data-no-row-nav]';

const OrderRow = memo(function OrderRow({
  order,
  columns,
  cellContext,
  isSelected,
  onToggle,
}) {
  const navigate = useNavigate();

  const handleToggle = useCallback(
    () => onToggle(order.order_id),
    [onToggle, order.order_id],
  );

  /*
   * Row click is a convenience layer, never the only way in. Three things it
   * has to refuse, or it becomes actively hostile:
   *   - clicks on the controls the row is full of;
   *   - the click that ends a drag-selection, so copying a customer name
   *     doesn't navigate away mid-gesture;
   *   - modifier clicks, which are handed to a real anchor or a new tab rather
   *     than swallowed into a same-tab navigation.
   */
  const handleRowClick = useCallback(
    (event) => {
      if (event.target.closest(INTERACTIVE)) return;
      if (window.getSelection()?.toString()) return;

      const href = `/orderview/${order.order_id}`;
      if (event.metaKey || event.ctrlKey || event.shiftKey) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(href);
    },
    [navigate, order.order_id],
  );

  return (
    <tr
      onClick={handleRowClick}
      className={`cursor-pointer border-b border-[#EEF3F4] last:border-b-0 transition-colors ${
        isSelected ? 'bg-[#F2F7F9]' : 'hover:bg-[#F7FAFA]'
      }`}
    >
      <td
        className="px-3.5 py-2.5 w-[34px]"
        onClick={(event) => event.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onChange={handleToggle}
          label={`Select order ${order.order_number}`}
        />
      </td>
      {columns.map((column) => (
        <React.Fragment key={column.key}>
          {column.cell(order, cellContext)}
        </React.Fragment>
      ))}
    </tr>
  );
});

const StateRow = ({ colSpan, children }) => (
  <tbody>
    <tr>
      <td colSpan={colSpan} className="px-4 py-14">
        {children}
      </td>
    </tr>
  </tbody>
);

const OrdersTable = ({
  orders,
  columns,
  cellContext,
  selectedIds,
  onToggleOne,
  onToggleAll,
  allSelected,
  partiallySelected,
  showSkeleton,
  isRefetching,
  isError,
  error,
  isEmpty,
  hasSearch,
  onRetry,
  onClearSearch,
}) => {
  const colSpan = columns.length + 1;
  // The selection column is prepended here so the skeleton lines up with the
  // real row without the caller having to describe it twice.
  const skeletonColumns = [{ skeleton: { width: '14px' } }, ...columns];

  return (
    <div className="bg-white border border-[#DFE6E9] rounded-xl overflow-hidden">
      <div className="overflow-x-auto relative">
        {/* A refetch keeps the previous rows and dims them, rather than
            swapping in a skeleton — the data is still valid, it is merely
            about to be replaced. */}
        {isRefetching && !showSkeleton && (
          <div
            className="absolute inset-0 z-10 bg-white/45 flex items-start justify-center pt-16 pointer-events-none"
            aria-hidden="true"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#DFE6E9] shadow-sm text-[12px] font-semibold text-[#3B4A54]">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </span>
          </div>
        )}

        <table
          className="w-full"
          aria-busy={showSkeleton || isRefetching ? 'true' : 'false'}
        >
          <caption className="sr-only">
            Orders. Amounts are in SAR, times in Riyadh.
          </caption>
          <thead>
            <tr className="bg-[#F3F6F6] border-b border-[#DFE6E9]">
              <th scope="col" className="px-3.5 py-2.5 w-[34px]">
                <Checkbox
                  checked={allSelected}
                  indeterminate={partiallySelected}
                  onChange={onToggleAll}
                  label="Select all orders on this page"
                />
              </th>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-3.5 py-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-[#6B7C86] whitespace-nowrap ${
                    column.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>

          {showSkeleton ? (
            <TableSkeleton columns={skeletonColumns} rows={8} />
          ) : isError ? (
            <StateRow colSpan={colSpan}>
              <div className="flex flex-col items-center text-center gap-3">
                <span
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#FBEDEC] text-[#B23A32]"
                  aria-hidden="true"
                >
                  <TriangleAlert className="w-5 h-5" />
                </span>
                <div>
                  <p className="text-[14px] font-semibold text-nysmah-dark m-0">
                    {error || 'Could not load orders.'}
                  </p>
                  <p className="text-[12.5px] text-[#6B7C86] m-0 mt-1">
                    The connection may have dropped. Nothing has been changed.
                  </p>
                </div>
                <Button variant="primary" onClick={onRetry}>
                  <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                  Try again
                </Button>
              </div>
            </StateRow>
          ) : isEmpty ? (
            <StateRow colSpan={colSpan}>
              <div className="flex flex-col items-center text-center gap-3">
                <Inbox className="w-8 h-8 text-[#C4CFD5]" aria-hidden="true" />
                <div>
                  <p className="text-[14px] font-semibold text-nysmah-dark m-0">
                    {hasSearch ? 'No orders match that search.' : 'No orders here yet.'}
                  </p>
                  {hasSearch && (
                    <p className="text-[12.5px] text-[#6B7C86] m-0 mt-1">
                      Try a different order number, customer or mobile.
                    </p>
                  )}
                </div>
                {hasSearch && (
                  <Button onClick={onClearSearch}>Clear search</Button>
                )}
              </div>
            </StateRow>
          ) : (
            <tbody>
              {orders.map((order) => (
                <OrderRow
                  key={order.order_id}
                  order={order}
                  columns={columns}
                  cellContext={cellContext}
                  isSelected={selectedIds.has(order.order_id)}
                  onToggle={onToggleOne}
                />
              ))}
            </tbody>
          )}
        </table>
      </div>
    </div>
  );
};

export default memo(OrdersTable);
